<script lang="ts">
  import { api, openChildTab } from '../../api/client';
  import type { PersonEntry } from '../../api/types';
  import { acting, auth, currentUser, managedBy, type Person, type User } from '../../stores/auth';
  import { confirm } from '../../stores/confirmDialog';
  import PageHeader from '../PageHeader.svelte';
  import { t } from '../../i18n/index.ts';

  // KidsMode is the account page's kids mode (spec 001): a regular member
  // puts their account in kids mode and a parent opens their children; a
  // child sees who manages them; a parent managing a child changes its
  // parents. Every member offered is one the account shares a group with
  // (001 FR-046).
  let people = $state<PersonEntry[]>([]);
  let parents = $state<Person[]>([]);
  let children = $state<Person[]>([]);
  let chosen = $state('');
  let password = $state('');
  let adding = $state(false);
  let error = $state('');

  const candidates = $derived(people.filter((p) => !parents.some((q) => q.user_id === p.user_id)));

  $effect(() => {
    if (!$currentUser) return;
    api.get<PersonEntry[]>('/me/people').then((p) => (people = p)).catch((e) => (error = e.message));
    if ($acting) {
      api.get<Person[]>('/me/parents').then((p) => (parents = p)).catch((e) => (error = e.message));
    } else {
      api.get<Person[]>('/me/children').then((c) => (children = c)).catch((e) => (error = e.message));
    }
  });

  async function reloadMe() {
    auth.setUser(await api.get<User>('/auth/me'));
  }

  // addParent enters kids mode on the member's password, or, for a parent
  // managing a child, adds another parent on the parent's own (001 FR-092, 001 FR-094).
  async function addParent(e: SubmitEvent) {
    e.preventDefault();
    const name = people.find((p) => p.user_id === chosen)?.display_name ?? '';
    if (!$acting && !(await confirm(t('componentsKidsEnterConfirm', { parent: name })))) return;
    try {
      parents = await api.post<Person[]>('/me/parents', { user_id: chosen, password });
      password = '';
      chosen = '';
      adding = false;
      error = '';
      await reloadMe();
    } catch (err: any) {
      error = err.message;
    }
  }

  async function removeParent(p: Person) {
    const self = p.user_id === $currentUser?.acting_parent?.user_id;
    const last = parents.length === 1;
    const child = $currentUser?.display_name ?? '';
    const msg = last
      ? t('componentsKidsRemoveLast', { parent: p.display_name, child })
      : self
        ? t('componentsKidsRemoveSelf', { child })
        : t('componentsKidsRemoveParent', { parent: p.display_name, child });
    if (!(await confirm(msg))) return;
    try {
      await api.delete(`/me/parents/${p.user_id}`);
      if (self) {
        window.close();
        window.location.href = '/settings';
        return;
      }
      parents = parents.filter((q) => q.user_id !== p.user_id);
    } catch (err: any) {
      error = err.message;
    }
  }

  // makeRegular removes every parent, handing the account back (001 FR-102).
  async function makeRegular() {
    if (!(await confirm(t('componentsKidsRegularConfirm', { child: $currentUser?.display_name ?? '' })))) return;
    try {
      await api.delete('/me/parents');
      window.close();
      window.location.href = '/settings';
    } catch (err: any) {
      error = err.message;
    }
  }

  // manage opens a child's settings in a new tab through an acting session (001 FR-100).
  async function manage(c: Person) {
    try {
      const token = api.post<{ access_token: string }>(`/me/children/${c.user_id}/session`).then((s) => s.access_token);
      if (!(await openChildTab(token))) error = t('componentsKidsPopupBlocked', { child: c.display_name });
    } catch (err: any) {
      error = err.message;
    }
  }
</script>

{#if $currentUser}
  {#if $acting}
    <div class="card p-5 mb-6">
      <PageHeader title={t('componentsKidsParents')} level="section">
        <button class="btn btn-danger btn-sm" onclick={makeRegular}>{t('componentsKidsMoveBack')}</button>
      </PageHeader>
      <p class="text-sm text-text-muted mb-3">{t('componentsKidsEachParent', { child: $currentUser.display_name })}</p>
      <ul class="divide-y mb-3" style="border-color: var(--theme-surface1);">
        {#each parents as p (p.user_id)}
          <li class="py-2 flex items-center gap-3">
            <span class="flex-1 font-medium">{p.display_name}{#if p.user_id === $currentUser.acting_parent?.user_id}<span class="text-xs text-text-muted ml-2">{t('componentsKidsYou')}</span>{/if}</span>
            <button class="btn btn-secondary btn-sm" onclick={() => removeParent(p)}>{t('appRemove')}</button>
          </li>
        {/each}
      </ul>
      {#if adding}
        {@render picker(t('componentsKidsYourPassword'), t('componentsKidsAddParent'))}
      {:else if candidates.length > 0}
        <button class="btn btn-secondary btn-sm" onclick={() => (adding = true)}><i aria-hidden="true" class="las la-user-plus mr-1"></i>{t('componentsKidsAddAParent')}</button>
      {/if}
    </div>
  {:else if $managedBy}
    <div class="card p-5 mb-6">
      <PageHeader title={t('componentsKidsTitle')} level="section" />
      <p class="text-sm"><i aria-hidden="true" class="las la-lock mr-1"></i>{t('componentsKidsManagedBy', { parents: $managedBy })}</p>
      <p class="text-sm text-text-muted mt-1">{t('componentsKidsChildNote')}</p>
    </div>
  {:else}
    {#if children.length > 0}
      <div class="card p-5 mb-6">
        <PageHeader title={t('componentsKidsChildren')} level="section" />
        <p class="text-sm text-text-muted mb-2">{t('componentsKidsChildrenIntro')}</p>
        <ul class="divide-y" style="border-color: var(--theme-surface1);">
          {#each children as c (c.user_id)}
            <li class="py-2 flex items-center gap-3">
              <span class="flex-1 font-medium">{c.display_name}</span>
              <button class="btn btn-secondary btn-sm" onclick={() => manage(c)}><i aria-hidden="true" class="las la-external-link-alt mr-1"></i>{t('componentsKidsManage')}</button>
            </li>
          {/each}
        </ul>
      </div>
    {:else}
      <div class="card p-5 mb-6">
        <PageHeader title={t('componentsKidsTitle')} level="section">
          {#if !adding && people.length > 0}
            <button class="btn btn-secondary btn-sm" onclick={() => (adding = true)}><i aria-hidden="true" class="las la-child mr-1"></i>{t('componentsKidsModeButton')}</button>
          {/if}
        </PageHeader>
        <p class="text-sm text-text-muted mb-3">{t('componentsKidsIntro')}</p>
        {#if adding}
          {@render picker(t('componentsKidsYourPassword'), t('componentsKidsPutInKidsMode'))}
        {/if}
      </div>
    {/if}
  {/if}
  {#if error}
    <p class="text-sm mb-4" style="color: var(--theme-error);">{error}</p>
  {/if}
{/if}

{#snippet picker(passwordLabel: string, action: string)}
  <form class="flex flex-wrap items-end gap-3" onsubmit={addParent}>
    <label class="text-sm">
      <span class="block mb-1">{t('componentsKidsParent')}</span>
      <select class="input w-56" bind:value={chosen} required>
        <option value="" disabled>{t('componentsKidsChooseMember')}</option>
        {#each candidates as p (p.user_id)}
          <option value={p.user_id}>{t('componentsKidsCandidate', { member: p.display_name, groups: p.groups.join(', ') })}</option>
        {/each}
      </select>
    </label>
    <label class="text-sm">
      <span class="block mb-1">{passwordLabel}</span>
      <input class="input w-48" type="password" autocomplete="current-password" bind:value={password} required />
    </label>
    <button type="submit" class="btn btn-primary btn-sm">{action}</button>
    <button type="button" class="btn btn-secondary btn-sm" onclick={() => { adding = false; password = ''; }}>{t('appCancel')}</button>
  </form>
{/snippet}
